import React, { useEffect, useState } from "react";
import { Seo, raceEventJsonLd, breadcrumbJsonLd } from "../components/Seo";
import { useParams, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { api, auth } from "../lib/api";
import { Card, PageHeader, Btn, Input, Textarea } from "../components/UI";
import { PartyBadge } from "./CandidateDirectory";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, Minus, Sparkles, Flag, Crown, ArrowLeft, Lightbulb, X, Megaphone } from "lucide-react";

const LINE_COLORS = { Democratic: "var(--partisan-blue)", Republican: "var(--partisan-red)", Independent: "var(--accent-primary)", Libertarian: "var(--warning)", Green: "var(--success)", Other: "var(--text-muted)" };

const MomentumIcon = ({ d }) => d === "rising" ? <TrendingUp className="w-4 h-4 text-emerald-300" /> : d === "falling" ? <TrendingDown className="w-4 h-4 text-rose-300" /> : <Minus className="w-4 h-4 text-slate-400" />;

export default function RacePage({ publicMode = false }) {
  const { raceId } = useParams();
  const navigate = useNavigate();
  const user = auth.getUser();
  const base = publicMode ? "/public/midterms" : "/midterms";
  const [race, setRace] = useState(null);
  const [aiTrend, setAiTrend] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [ideas, setIdeas] = useState(null);
  const [ideasFor, setIdeasFor] = useState(null);
  const [ideasLoading, setIdeasLoading] = useState(false);
  const [claimTarget, setClaimTarget] = useState(null);
  const [claimForm, setClaimForm] = useState({ official_email: "", fec_id_confirmation: "", message: "" });
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get(`${base}/races/${raceId}`).then(r => setRace(r.data)).catch(() => toast.error("Race not found"));
  }, [raceId, base]);

  const runAiTrend = async () => {
    if (publicMode) { navigate("/login"); return; }
    setAiLoading(true);
    try {
      const r = await api.post(`/midterms/races/${raceId}/ai-trends`);
      setAiTrend(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI analysis failed"); }
    finally { setAiLoading(false); }
  };

  const runIdeas = async (c) => {
    if (publicMode) { navigate("/login"); return; }
    setIdeasFor(c); setIdeasLoading(true); setIdeas(null);
    try {
      const r = await api.post(`/midterms/candidates/${c.id}/ai-marketing`);
      setIdeas(r.data.ideas);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI ideas failed"); }
    finally { setIdeasLoading(false); }
  };

  const submitClaim = async () => {
    if (!claimForm.official_email) { toast.error("Official campaign email required"); return; }
    setClaiming(true);
    try {
      await api.post(`/midterms/candidates/${claimTarget.id}/claim`, claimForm);
      toast.success("Claim submitted — an admin will review it shortly");
      setClaimTarget(null);
      const r = await api.get(`${base}/races/${raceId}`); setRace(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Claim failed"); }
    finally { setClaiming(false); }
  };

  if (!race) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading race…</div>;

  const trackedIds = new Set(race.momentum.map(m => m.fec_candidate_id));
  const byDate = {};
  race.polls.forEach(p => {
    const d = byDate[p.date] = byDate[p.date] || { date: p.date.slice(5), _counts: {} };
    Object.entries(p.samples).forEach(([cid, v]) => {
      d[cid] = (d[cid] || 0) + v;
      d._counts[cid] = (d._counts[cid] || 0) + 1;
    });
  });
  const chartData = Object.values(byDate).map(d => {
    const row = { date: d.date };
    Object.keys(d._counts).forEach(cid => { row[cid] = +(d[cid] / d._counts[cid]).toFixed(1); });
    return row;
  });
  const tracked = race.candidates.filter(c => trackedIds.has(c.fec_candidate_id));
  const shortName = (n) => n.split(",")[0];

  const raceTitle = `${race.office_full} — ${race.state}${race.office_full === "House" ? `-${race.district}` : ""}`;
  return (
    <div className="p-6 lg:p-8 space-y-6">
      {publicMode && <Seo title={`2026 ${raceTitle} race — candidates, FEC money & momentum`} description={`${race.candidates.length} declared candidates in the 2026 ${raceTitle} race with live FEC fundraising, cash on hand and momentum tracking.`} path={`/directory/race/${raceId}`}
        jsonLd={[raceEventJsonLd({ label: `U.S. ${race.office_full} — ${race.state}${race.office_full === "House" ? `-${race.district}` : ""}`, state: race.state, state_name: race.state }, `/directory/race/${raceId}`), breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Directory", path: "/directory" }, { name: raceTitle, path: `/directory/race/${raceId}` }])]} />}
      <button onClick={() => navigate(publicMode ? "/directory" : "/midterms")} className="text-xs text-slate-400 hover:text-cyan-300 flex items-center gap-1" data-testid="back-to-directory">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to directory
      </button>
      <PageHeader
        eyebrow={`2026 Midterms · ${race.region}`}
        title={`${race.office_full} — ${race.state}${race.office_full === "House" ? `-${race.district}` : ""}`}
        subtitle={`${race.candidates.length} declared candidates · live FEC roster · demo tracking polls`}
        action={
          <Btn onClick={runAiTrend} disabled={aiLoading} data-testid="ai-trend-btn">
            {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI trend analysis
          </Btn>
        }
      />

      {aiTrend && (
        <Card className="border-cyan-400/30" data-testid="ai-trend-panel">
          <div className="text-xs uppercase tracking-wider text-cyan-300 font-medium mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Race Analysis</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{aiTrend.analysis}</div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="poll-chart-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold">Polling trend — 10 week tracking</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 uppercase tracking-wide">Demo poll data</span>
          </div>
          <div className="h-72">
            {chartData.length === 0 || tracked.length < 2 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500" data-testid="poll-chart-empty">
                Not enough declared candidates for a trend view — polling starts once a race has 2+ candidates.
              </div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="rgb(var(--contrast-rgb) / 0.06)" />
                <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
                <YAxis domain={["auto", "auto"]} tick={{ fill: "var(--text-muted)", fontSize: 11 }} unit="%" />
                <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--contrast-rgb) / 0.1)", borderRadius: 8, fontSize: 12 }} />
                <Legend formatter={(v) => { const c = tracked.find(x => x.fec_candidate_id === v); return c ? shortName(c.name) : v; }} />
                {tracked.map(c => (
                  <Line key={c.fec_candidate_id} type="monotone" dataKey={c.fec_candidate_id} stroke={LINE_COLORS[c.party] || LINE_COLORS.Other} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card data-testid="momentum-card">
          <h3 className="font-display text-lg font-bold mb-3">Momentum</h3>
          {race.momentum.length === 0 && <div className="text-sm text-slate-500 py-4">No tracking data yet for this race.</div>}
          <div className="space-y-3">
            {race.momentum.map(m => (
              <div key={m.fec_candidate_id} className="flex items-center gap-3 border-b border-white/5 pb-2.5">
                <MomentumIcon d={m.direction} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{m.name}</div>
                  <div className="flex items-center gap-2 mt-0.5"><PartyBadge party={m.party} /></div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold">{m.current_support}%</div>
                  <div className={`text-xs ${m.delta > 0 ? "text-emerald-300" : m.delta < 0 ? "text-rose-300" : "text-slate-500"}`}>{m.delta > 0 ? "+" : ""}{m.delta} pts</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card padded={false}>
        <div className="px-5 py-3 border-b border-white/10">
          <h3 className="font-display text-lg font-bold">All declared candidates</h3>
        </div>
        <div className="divide-y divide-white/5" data-testid="race-candidates-list">
          {race.candidates.map(c => (
            <div key={c.id} className="px-5 py-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium">{c.name}</span>
                  {c.claim_status === "approved" && <span className="inline-flex items-center gap-1 text-[10px] text-cyan-300"><Crown className="w-3 h-3" /> Claimed</span>}
                  {c.claim_status === "pending" && <span className="text-[10px] text-amber-300">Claim pending</span>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <PartyBadge party={c.party} />
                  <span className="text-xs text-slate-500">{c.incumbent_challenge || "—"} · FEC {c.fec_candidate_id}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Btn variant="secondary" className="!px-3 !py-1.5 text-xs" onClick={() => runIdeas(c)} data-testid={`ideas-btn-${c.id}`}>
                  <Lightbulb className="w-3.5 h-3.5" /> Marketing ideas
                </Btn>
                {c.claim_status !== "approved" && c.claim_status !== "pending" && (
                  <Btn className="!px-3 !py-1.5 text-xs" onClick={() => publicMode ? navigate("/login") : setClaimTarget(c)} data-testid={`claim-btn-${c.id}`}>
                    <Flag className="w-3.5 h-3.5" /> Claim this page
                  </Btn>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {ideasFor && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setIdeasFor(null)}>
          <div className="glass-card rounded-lg p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ideas-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-cyan-300 font-medium flex items-center gap-2"><Megaphone className="w-4 h-4" /> AI Marketing Recommendations</div>
                <h3 className="font-display text-xl font-bold mt-1">{ideasFor.name}</h3>
              </div>
              <button onClick={() => setIdeasFor(null)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            {ideasLoading ? (
              <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Claude is analyzing the race…</div>
            ) : (
              <div className="space-y-3">
                {(ideas || []).map((idea, i) => (
                  <div key={i} className="border border-white/10 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-display font-bold text-sm">{idea.title}</div>
                      <div className="flex gap-1.5 shrink-0">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300">{idea.channel}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/15 text-slate-300">{idea.urgency}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-1.5">{idea.rationale}</p>
                    {idea.suggested_play && <p className="text-xs text-cyan-200/80 mt-1.5">▸ {idea.suggested_play}</p>}
                  </div>
                ))}
                <div className="pt-2 flex justify-end">
                  <Btn onClick={() => navigate("/campaigns/ctv/new")} data-testid="ideas-launch-ctv-btn">Launch CTV campaign →</Btn>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {claimTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setClaimTarget(null)}>
          <div className="glass-card rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="claim-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-bold">Claim this page</h3>
                <p className="text-xs text-slate-400 mt-1">{claimTarget.name} · verified by an admin before approval</p>
              </div>
              <button onClick={() => setClaimTarget(null)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <Input label="Official campaign email *" value={claimForm.official_email} onChange={e => setClaimForm({ ...claimForm, official_email: e.target.value })} placeholder="press@campaign.org" data-testid="claim-email-input" />
              <Input label="FEC ID confirmation" value={claimForm.fec_id_confirmation} onChange={e => setClaimForm({ ...claimForm, fec_id_confirmation: e.target.value })} placeholder={claimTarget.fec_candidate_id} data-testid="claim-fecid-input" />
              <Textarea label="Message to reviewer" value={claimForm.message} onChange={e => setClaimForm({ ...claimForm, message: e.target.value })} placeholder="I'm the campaign manager for…" data-testid="claim-message-input" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setClaimTarget(null)}>Cancel</Btn>
              <Btn onClick={submitClaim} disabled={claiming} data-testid="submit-claim-btn">
                {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} Submit claim
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
